import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Building2, User, Mail, Lock, Eye, EyeOff } from '../icons/wp-icons';
import { getHostBranding } from '../utils/hostBranding';
import HostBrandedNotice from '../components/HostBrandedNotice';

function Register() {
  const hostBranding = useMemo(() => getHostBranding(), []);
  const [firmenname, setFirmenname] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();

  // Theme-Color für Register-Seite setzen (nicht für Whitelabel-Hosts)
  useEffect(() => {
    if (hostBranding) return;
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    const originalColor = metaThemeColor?.getAttribute('content');

    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', '#378ab2');
    }

    return () => {
      if (metaThemeColor && originalColor) {
        metaThemeColor.setAttribute('content', originalColor);
      }
    };
  }, [hostBranding]);

  // Auf Whitelabel-Hosts findet keine Self-Registration statt — Accounts werden
  // von HeyKiki per /api/heykiki/provision angelegt.
  if (hostBranding?.passwordFlowHandledExternally) {
    return (
      <HostBrandedNotice
        hostBranding={hostBranding}
        pageTitle="Registrierung"
        title="Selbst-Registrierung nicht verfügbar"
        message={`Neue Accounts werden direkt von ${hostBranding.supportContact} angelegt. Wenn du noch keinen Zugang hast, wende dich bitte an den ${hostBranding.supportContact}-Support.`}
      />
    );
  }

  // Wenn User bereits eingeloggt ist, zum Dashboard weiterleiten
  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const validateForm = () => {
    if (!firmenname.trim()) {
      setError('Bitte geben Sie einen Firmennamen ein');
      return false;
    }
    if (!name.trim()) {
      setError('Bitte geben Sie Ihren Namen ein');
      return false;
    }
    if (!email.trim()) {
      setError('Bitte geben Sie eine E-Mail-Adresse ein');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Bitte geben Sie eine gueltige E-Mail-Adresse ein');
      return false;
    }
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein');
      return false;
    }
    if (password !== passwordConfirm) {
      setError('Die Passwoerter stimmen nicht ueberein');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const result = await register(firmenname.trim(), name.trim(), email.trim().toLowerCase(), password);

    if (result.success) {
      if (result.needsVerification) {
        // E-Mail-Verifizierung erforderlich
        navigate('/verify-email', { state: { email: result.email }, replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="bg-surface p-8 rounded-card shadow-card w-full max-w-md">
        <div className="text-center mb-6">
          <img
            src="/icons/icon-512x512.png"
            alt="WerkPilot Logo"
            className="w-24 h-24 mx-auto mb-4"
          />
          <span className="block text-2xl font-bold text-gray-900" style={{ fontFamily: "'Montserrat', sans-serif" }}>WerkPilot</span>
          <p className="text-text-muted">Kostenlos registrieren</p>
        </div>

        {/* Trial Info */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800 text-center">
            <strong>14 Tage kostenlos testen</strong><br />
            Keine Kreditkarte erforderlich
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Firmenname */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Firmenname</label>
            <div className="relative">
              <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={firmenname}
                onChange={(e) => setFirmenname(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
                placeholder="Ihre Firma GmbH"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Ihr Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
                placeholder="Max Mustermann"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">E-Mail</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
                placeholder="max@firma.de"
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Passwort */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Passwort</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
                placeholder="Mindestens 8 Zeichen"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Passwort bestaetigen */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Passwort bestaetigen</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
                placeholder="Passwort wiederholen"
                required
                disabled={loading}
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full bg-[#378ab2] text-white py-3 rounded-button font-medium hover:bg-[#2a6d8a] transition disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading}
          >
            {loading ? 'Registrierung laeuft...' : 'Kostenlos registrieren'}
          </button>
        </form>

        {/* Login Link */}
        <div className="mt-6 pt-6 border-t border-border text-center">
          <p className="text-sm text-text-muted">
            Bereits registriert?{' '}
            <Link to="/login" className="text-[#378ab2] hover:underline font-medium">
              Anmelden
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
